import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import * as React from "react";
import { Button } from "@volca/ui/components/ui/button";
import { useBreakpoint } from "@volca/ui/hooks/use-breakpoint";
import { cn } from "@volca/ui/lib/utils";

const SIMPLE_SIDEBAR_DEFAULT_WIDTH = "436px";

type SimpleSidebarContext = {
  open: boolean;
  setOpen: (open: boolean) => void;
  width: string;
};

const SimpleSidebarContext = React.createContext<SimpleSidebarContext | null>(
  null,
);

const useSimpleSidebar = () => {
  const context = React.useContext(SimpleSidebarContext);
  if (!context) {
    throw new Error(
      "useSimpleSidebar must be used within a SimpleSidebarProvider",
    );
  }
  return context;
};

interface SimpleSidebarLayoutProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  sidebarWidth?: string;
  className?: string;
}

const SimpleSidebarLayout = React.forwardRef<
  HTMLDivElement,
  SimpleSidebarLayoutProps
>(
  (
    {
      children,
      open: controlledOpen,
      onOpenChange,
      defaultOpen = false,
      sidebarWidth = SIMPLE_SIDEBAR_DEFAULT_WIDTH,
      className,
      ...props
    },
    ref,
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = React.useCallback(
      (value: boolean) => {
        onOpenChange?.(value);
        setUncontrolledOpen(value);
      },
      [onOpenChange],
    );

    React.useEffect(() => {
      if (!open) return;
      document.body.dataset.pylonOffset = "true";
      document.body.style.setProperty("--pylon-sidebar-width", sidebarWidth);
      return () => {
        delete document.body.dataset.pylonOffset;
        document.body.style.removeProperty("--pylon-sidebar-width");
      };
    }, [open, sidebarWidth]);

    const value = React.useMemo(
      () => ({
        open,
        setOpen,
        width: sidebarWidth,
      }),
      [open, setOpen, sidebarWidth],
    );

    return (
      <SimpleSidebarContext.Provider value={value}>
        <div
          ref={ref}
          className={cn("relative h-page w-full overflow-x-clip", className)}
          style={
            {
              "--simple-sidebar-width": sidebarWidth,
            } as React.CSSProperties
          }
          {...props}
        >
          {children}
        </div>
      </SimpleSidebarContext.Provider>
    );
  },
);
SimpleSidebarLayout.displayName = "SimpleSidebarLayout";

const SimpleSidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & {
    scrollAreaClassName?: string;
    removeScrollArea?: boolean;
  }
>(
  (
    { className, children, scrollAreaClassName, removeScrollArea, ...props },
    ref,
  ) => {
    const { open, width } = useSimpleSidebar();

    return (
      <div
        ref={ref}
        data-slot="simple-sidebar-content"
        data-state={open ? "open" : "closed"}
        className={cn("h-full", className)}
        style={
          {
            "--simple-sidebar-content-width": open
              ? `calc(100% - ${width})`
              : "100%",
          } as React.CSSProperties
        }
        {...props}
      >
        {removeScrollArea ? (
          children
        ) : (
          <div
            className={cn("h-full w-full overflow-auto", scrollAreaClassName)}
          >
            {children}
          </div>
        )}
      </div>
    );
  },
);
SimpleSidebarContent.displayName = "SimpleSidebarContent";

const SimpleSidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, children, ...props }, ref) => {
  const { open, width } = useSimpleSidebar();
  const isMobile = useBreakpoint() === "mobile";

  return (
    <div
      ref={ref}
      data-slot="simple-sidebar"
      data-state={open ? "open" : "closed"}
      inert={!open}
      className={cn(
        "absolute inset-y-0 right-0 overflow-hidden border-l bg-surface",
        "z-20",
        "data-[state=closed]:translate-x-full data-[state=closed]:shadow-none",
        "flex flex-col",
        className,
      )}
      style={{ width: isMobile ? "100%" : width }}
      {...props}
    >
      {children}
    </div>
  );
});
SimpleSidebar.displayName = "SimpleSidebar";

const SimpleSidebarHeader = ({
  title,
  description,
  actions,
  onClose,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  onClose?: () => void;
  className?: string;
}) => (
  <div
    className={cn(
      "flex w-full shrink-0 items-start gap-3 px-5 pt-5 pb-4",
      className,
    )}
  >
    <div className="min-w-0 flex-1">
      <div className="flex min-h-8 min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="min-w-0 text-lg font-medium text-pretty wrap-break-word text-ink">
          {title}
        </h2>
        {actions}
      </div>
      {description && (
        <p className="mt-0.5 text-sm text-subtle">{description}</p>
      )}
    </div>
    {onClose && (
      <Button
        variant="ghost"
        size="icon"
        aria-label="Close"
        onClick={onClose}
        className="shrink-0 text-subtle"
      >
        <XIcon aria-hidden />
      </Button>
    )}
  </div>
);

export {
  SimpleSidebarLayout,
  SimpleSidebarContent,
  SimpleSidebar,
  SimpleSidebarHeader,
  useSimpleSidebar,
};
